import { zodResolver } from "@hookform/resolvers/zod";
import { AllocationDetailsFormFields } from "features/allocations/AllocationDetailsFormFields";
import {
  useAllocationRuleList,
  useAllocationRuleRetrieveSuspense,
  useAllocationRuleUpdate,
} from "gen/endpoints/allocation-rule/allocation-rule";
import { AllocationRuleEditDataRequest } from "gen/model/allocationRuleEditDataRequest";
import { allocationRuleUpdateBody } from "gen/schemas/allocation-rule/allocation-rule";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { isTruthy } from "shared/utils/utils";

interface AllocationEditDrawerProps {
  fundFamilyId: string;
  allocationRuleId: string;
  onCloseComplete: () => void;
}

export const AllocationEditDrawer: React.FC<AllocationEditDrawerProps> = ({
  fundFamilyId,
  allocationRuleId,
  onCloseComplete,
}) => {
  const { t } = useTranslation(["allocation"]);

  const { data: allocationRule } = useAllocationRuleRetrieveSuspense(
    fundFamilyId,
    allocationRuleId,
  );

  const { data: entityAllocationData } = useAllocationRuleList(fundFamilyId, {
    entity_id: allocationRule.entity_id,
  });
  // Multiple allocation rules for an entity can't point to the same transaction calculation
  const existingTransactionCalculationIds = entityAllocationData
    ?.map((allocation) => allocation.transaction_calculation_id)
    .filter((id) => id !== allocationRule.transaction_calculation_id)
    .filter(isTruthy);

  const { mutateAsync: updateAllocationRule } = useAllocationRuleUpdate();

  const form = useForm<AllocationRuleEditDataRequest>({
    resolver: zodResolver(allocationRuleUpdateBody),
    defaultValues: allocationRule,
  });

  const onSubmit = async (data: AllocationRuleEditDataRequest) =>
    await updateAllocationRule({
      fundFamilyId,
      allocationRuleId,
      data,
    });

  return (
    <FormDrawer
      title={t("allocation:edit.title")}
      formProps={{
        form,
        onSubmit,
        successMessage: t("allocation:edited"),
      }}
      onCloseComplete={onCloseComplete}
    >
      <AllocationDetailsFormFields
        fundFamilyId={fundFamilyId}
        calculationIdsToIgnore={existingTransactionCalculationIds}
      />
    </FormDrawer>
  );
};
